import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Storefront, CheckCircle, XCircle } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  company_name: "", contact_name: "", contact_email: "", country: "",
  website: "", category: "TMS", pitch: "", logo_url: "", plan: "vendor_yearly",
};

export function VendorApply() {
  const [form, setForm] = useState(initial);
  const [plans, setPlans] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/portal/vendors/plans`)
      .then(({ data }) => setPlans(data.plans || []))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_email || !form.country) {
      toast.error("Company, contact email and country are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/portal/vendors/signup`, {
        ...form,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-16 px-6" data-testid="vendor-apply-page">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <div className="overline mb-2">EPAS · Vendor Directory</div>
          <h1 className="font-display text-4xl font-bold text-navy tracking-tight flex items-center gap-3">
            <Storefront size={32} weight="duotone" className="text-ember" /> Apply to be listed
          </h1>
          <p className="text-navy/70 mt-3 max-w-xl">
            The EPAS Vendor Directory is a curated shortlist of tools that our member forwarders use daily —
            TMS, CRM, quoting AI, insurance, fintech. Listings are paid, capped per category, and reviewed by
            EPAS operations. No pay-to-play spam.
          </p>
        </div>

        <form onSubmit={submit} data-testid="vendor-apply-form" className="border border-black/10 bg-white p-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block text-xs uppercase tracking-widest text-navy/60 md:col-span-2">Company name *
            <input className="input mt-1" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="vendor-company" />
          </label>
          <label className="block text-xs uppercase tracking-widest text-navy/60">Contact name
            <input className="input mt-1" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="vendor-contact-name" />
          </label>
          <label className="block text-xs uppercase tracking-widest text-navy/60">Contact email *
            <input className="input mt-1" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="vendor-contact-email" />
          </label>
          <label className="block text-xs uppercase tracking-widest text-navy/60">Country *
            <input className="input mt-1" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="vendor-country" />
          </label>
          <label className="block text-xs uppercase tracking-widest text-navy/60">Website
            <input className="input mt-1" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" data-testid="vendor-website" />
          </label>
          <label className="block text-xs uppercase tracking-widest text-navy/60">Category
            <select className="input mt-1" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="vendor-category">
              <option>TMS</option>
              <option>CRM</option>
              <option>Freight AI / Quoting</option>
              <option>Cargo Insurance</option>
              <option>Fintech / Payments</option>
              <option>Compliance</option>
              <option>Other</option>
            </select>
          </label>
          <label className="block text-xs uppercase tracking-widest text-navy/60">Logo URL
            <input className="input mt-1" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://" data-testid="vendor-logo" />
          </label>
          <label className="block text-xs uppercase tracking-widest text-navy/60 md:col-span-2">Pitch (one-liner)
            <textarea className="input mt-1 min-h-[100px]" value={form.pitch} onChange={(e) => setForm({ ...form, pitch: e.target.value })} placeholder="What EPAS members get from your product. One or two sentences." data-testid="vendor-pitch" />
          </label>

          <div className="md:col-span-2">
            <div className="overline mb-3">Listing plan</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {plans.map((p) => (
                <label key={p.key}
                  className={`border p-4 cursor-pointer transition-colors ${form.plan === p.key ? "border-navy bg-navy/5" : "border-black/10 hover:border-navy/50"}`}
                  data-testid={`vendor-plan-${p.key}`}>
                  <input type="radio" name="plan" className="mr-2" checked={form.plan === p.key} onChange={() => setForm({ ...form, plan: p.key })} />
                  <span className="font-medium text-navy">{p.label}</span>
                  <div className="text-lg font-display font-bold text-navy mt-2">${p.amount_usd.toLocaleString()}<span className="text-sm font-normal text-navy/60"> / {p.interval}</span></div>
                </label>
              ))}
            </div>
          </div>

          <button type="submit" disabled={submitting} data-testid="vendor-submit"
            className="md:col-span-2 bg-navy text-white px-6 py-3 text-xs uppercase tracking-widest hover:bg-navy/90 disabled:opacity-50">
            {submitting ? "Redirecting to Stripe…" : "Continue to secure checkout"}
          </button>
          <p className="md:col-span-2 text-[11px] text-navy/50 -mt-2">
            Payment via Stripe. Cards, SEPA, most local methods. Listing activates on successful payment.
            Cancel any time — you keep access through the paid period.
          </p>
        </form>

        <button onClick={() => navigate(-1)} className="text-xs uppercase tracking-widest text-navy/60 mt-6 hover:text-ember">← Back</button>
      </div>
    </div>
  );
}

export function VendorSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/portal/vendors/checkout-status/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        if (data.payment_status === "paid" || data.payment_status === "failed" || attempts >= 20) return;
        timer = setTimeout(() => { setAttempts((a) => a + 1); poll(); }, 2000);
      } catch {
        if (attempts < 20) timer = setTimeout(() => { setAttempts((a) => a + 1); poll(); }, 2000);
      }
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-6" data-testid="vendor-success-page">
      <div className="max-w-md w-full border border-black/10 bg-white p-8 text-center">
        {status.payment_status === "paid" ? (
          <>
            <CheckCircle size={48} weight="duotone" className="text-emerald-600 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-navy mt-4">Listing activated</h1>
            <p className="text-sm text-navy/70 mt-3">
              Payment confirmed. Your EPAS vendor listing is now live in the member portal. We'll email you the
              admin link and next steps.
            </p>
          </>
        ) : status.payment_status === "failed" ? (
          <>
            <XCircle size={48} weight="duotone" className="text-red-600 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-navy mt-4">Payment failed</h1>
            <p className="text-sm text-navy/70 mt-3">The checkout didn't complete. No charge was made.</p>
          </>
        ) : (
          <>
            <div className="animate-spin w-8 h-8 border-2 border-navy border-t-transparent rounded-full mx-auto"></div>
            <h1 className="font-display text-2xl font-bold text-navy mt-4">Confirming payment…</h1>
            <p className="text-sm text-navy/70 mt-3">Stripe usually confirms within a few seconds.</p>
          </>
        )}
        <a href="/" className="inline-block text-xs uppercase tracking-widest text-navy/60 mt-6 hover:text-ember">Back to epas.one</a>
      </div>
    </div>
  );
}

export function VendorCancel() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-6" data-testid="vendor-cancel-page">
      <div className="max-w-md w-full border border-black/10 bg-white p-8 text-center">
        <XCircle size={48} weight="duotone" className="text-navy/40 mx-auto" />
        <h1 className="font-display text-2xl font-bold text-navy mt-4">Checkout cancelled</h1>
        <p className="text-sm text-navy/70 mt-3">No charge was made. You can restart the application any time.</p>
        <a href="/vendors/apply" className="inline-block text-xs uppercase tracking-widest text-navy hover:text-ember mt-6 border border-navy px-4 py-2">Try again</a>
      </div>
    </div>
  );
}
